// Council voices: each deity's line is spoken through /api/tts in its own Fish voice, one at a time
// (Bothread "Conference": new replies queue and auto-play; any message can be played or paused).
import { Platform } from 'react-native';
import { Audio, type AVPlaybackStatus } from 'expo-av';
import { apiFetch } from '../lib/api';

// Fish Audio reference ids. The first seven are Joel's onboarding picks (apps/mobile/scripts/onboarding/voices.json);
// the rest borrow the nearest of those until they get their own.
const ATLAS = 'e422370a73e4439b8ccc10d58b78819b';
const KYRS = '802e3bc2b27e49c2995d23ef70e6ac89';
const KRNOS = 'fa33e814a00d48dc8cde47c0d3b35a73';
const METIS = 'b545c585f631496c914815291da4e893';
const CLIO = 'c635962304d6495394063a241bfd1014';
export const COUNCIL_VOICES: Record<string, string> = {
  syqi: 'c19223ecb9d043708c65c2b6545e189e',
  telos: ATLAS,
  oracle: ATLAS,
  poros: ATLAS,
  argos: ATLAS,
  kyrs: KYRS,
  hrmez: KYRS,
  krnos: KRNOS,
  tkne: KRNOS,
  janus: KRNOS,
  nemos: KRNOS,
  metis: METIS,
  astra: METIS,
  themis: METIS,
  clio: CLIO,
  // NUMA, the steady breath-keeper: his own calm, gentle, middle-aged male voice (from the audition
  // shortlist, audio/shape-voice-audition). He borrowed CLIO's female voice before (Joel, 2026-10-17).
  numa: '0b74ead073f2474a904f69033535b98e',
  moyri: CLIO,
  // SHAPE deities (shapeRoster.ts): Joel's audition picks (audio/shape-voice-audition) where chosen;
  // the rest reuse a voice by gender (elders KRNOS / CLIO) until they get their own.
  hypnos: 'c97c6987eaf944d5b738c592c82ed0e9',
  horkos: KRNOS,
  plutus: ATLAS,
  kerdos: '648302ff56f54b05a5f3524fbadfb333',
  agon: KYRS,
  filot: METIS,
  xaris: '5c8dc6a69c0b4edfb32634db6384bf34',
  monoi: CLIO,
  irene: CLIO,
  pitho: METIS,
  hyga: METIS,
  keso: CLIO,
  bia: METIS,
  syka: CLIO,
  urax: METIS,
  praxi: '7b0e224b87454737a05e573d318e89ba',
  pistis: METIS,
  prono: CLIO,
  meta: '7e06539ec26b4db5ba3f11d102ead0ee',
  frosy: METIS,
  elis: METIS,
  aidos: CLIO,
  pyda: METIS,
  uthen: CLIO,
  thera: METIS,
};

export interface SpeakItem {
  id: string;
  speaker: string;
  text: string;
}
export interface PlaybackState {
  id: string | null;
  paused: boolean;
  loading: boolean;
}

/** Speech-friendly text: no markdown, whitespace collapsed. */
const clean = (s: string) => s.replace(/[*_#`~>|[\]]/g, '').replace(/\s+/g, ' ').trim();
/** Capped so one (non-chunked) reply is one TTS request. */
const speakable = (s: string) => clean(s).slice(0, 880);

// ---- Sentence chunking (opt-in, for long spoken rundowns) -------------------------------------
const FIRST_MIN = 40; // first chunk: first sentence, merged with the next while shorter than this
const MAX_CHUNK = 260; // later chunks: up to this many chars...
const MAX_SENTENCES = 3; // ...and up to this many sentences
const AHEAD = 2; // chunks downloading ahead of the one playing

const ABBREV = /^(mr|mrs|ms|dr|st|prof|vs|e\.g|i\.e)$/i;

/** Split at sentence ends (. ! ? … followed by whitespace/end), skipping abbreviations and initials. */
function sentences(text: string): string[] {
  const out: string[] = [];
  const n = text.length;
  let start = 0;
  for (let i = 0; i < n; i++) {
    const c = text[i];
    if (c !== '.' && c !== '!' && c !== '?' && c !== '…') continue;
    let j = i + 1;
    while (j < n && /[.!?…]/.test(text[j])) j++;
    while (j < n && /["'”’)\]]/.test(text[j])) j++;
    if (j >= n || /\s/.test(text[j])) {
      const word = text.slice(start, i).match(/(\S+)$/)?.[1] ?? '';
      const abbrev = c === '.' && j === i + 1 && (ABBREV.test(word) || /^[A-Za-z]$/.test(word));
      if (!abbrev) {
        const s = text.slice(start, j).trim();
        if (s) out.push(s);
        start = j;
      }
    }
    i = j - 1;
  }
  const tail = text.slice(start).trim();
  if (tail) out.push(tail);
  return out;
}

/** A sentence longer than MAX_CHUNK is cut at the last clause break (or space) inside the limit. */
function breakLong(s: string): string[] {
  const out: string[] = [];
  while (s.length > MAX_CHUNK) {
    const win = s.slice(0, MAX_CHUNK);
    let cut = Math.max(win.lastIndexOf(', '), win.lastIndexOf('; '), win.lastIndexOf(': '), win.lastIndexOf(' — '));
    if (cut < 80) cut = win.lastIndexOf(' ');
    if (cut <= 0) cut = MAX_CHUNK - 1;
    out.push(s.slice(0, cut + 1).trim());
    s = s.slice(cut + 1).trim();
  }
  if (s) out.push(s);
  return out;
}

/**
 * Deterministic speech chunks: chunk 0 is the first sentence (merged with following sentences while under
 * ~40 chars) so it synthesizes fast; later chunks pack 1-3 sentences up to ~260 chars.
 */
export function splitSpeech(text: string): string[] {
  const units = sentences(clean(text)).flatMap(breakLong);
  if (!units.length) return [];
  let first = units[0];
  let i = 1;
  while (first.length < FIRST_MIN && i < units.length && first.length + 1 + units[i].length <= MAX_CHUNK) {
    first += ' ' + units[i++];
  }
  const chunks = [first];
  let cur = '';
  let count = 0;
  for (; i < units.length; i++) {
    const u = units[i];
    if (cur && (count >= MAX_SENTENCES || cur.length + 1 + u.length > MAX_CHUNK)) {
      chunks.push(cur);
      cur = '';
      count = 0;
    }
    cur = cur ? cur + ' ' + u : u;
    count++;
  }
  if (cur) chunks.push(cur);
  return chunks;
}

async function fetchAudio(speaker: string, text: string): Promise<string> {
  const r = await apiFetch<{ audio: string; mime?: string }>('/api/tts?withLipsync=1', {
    method: 'POST',
    body: JSON.stringify({ text, referenceId: COUNCIL_VOICES[speaker] }),
  });
  return `data:${r.mime ?? 'audio/mpeg'};base64,${r.audio}`;
}

const CACHE_MAX = 40; // base64 clips kept; oldest evicted first

interface Part {
  key: string; // cache key: item.id, or `${item.id}#${i}` for chunks
  speaker: string;
  text: string;
}
interface Clip {
  play(): Promise<void>;
  pause(): Promise<void> | void;
  resume(): Promise<void> | void;
  dispose(): Promise<void>; // idempotent; also settles `ended`
  ended: Promise<void>; // finished, errored or disposed
}

/** Load one clip (web: HTMLAudio, native: expo-av) without starting it. Throws if it can't load. */
async function loadClip(uri: string): Promise<Clip> {
  let end!: () => void;
  const ended = new Promise<void>((r) => {
    end = r;
  });
  if (Platform.OS === 'web') {
    const a = new window.Audio(uri);
    a.onended = end;
    a.onerror = end;
    let gone = false;
    // Watchdog: a clip that never reports 'ended' (stalled decode, lost event) must not stall the round.
    let timer: ReturnType<typeof setTimeout> | null = null;
    const disarm = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };
    const arm = () => {
      disarm();
      if (gone || a.paused || !(Number.isFinite(a.duration) && a.duration > 0)) return;
      timer = setTimeout(end, (a.duration - a.currentTime) * 1000 + 1500);
    };
    a.onloadedmetadata = arm;
    a.onplaying = arm;
    return {
      ended,
      play: async () => {
        await a.play().catch(end);
        arm();
      },
      pause: () => {
        a.pause();
        disarm();
      },
      resume: () => {
        void a.play().then(arm, end);
      },
      dispose: async () => {
        if (gone) return;
        gone = true;
        disarm();
        a.onended = null;
        a.onerror = null;
        a.onloadedmetadata = null;
        a.onplaying = null;
        a.pause();
        a.src = '';
        end();
      },
    };
  }
  const { sound } = await Audio.Sound.createAsync({ uri }, { shouldPlay: false });
  sound.setOnPlaybackStatusUpdate((st: AVPlaybackStatus) => {
    // didJustFinish can be missed between status ticks; reaching the end position counts too.
    if (st.isLoaded ? st.didJustFinish || (!!st.durationMillis && st.positionMillis >= st.durationMillis - 30 && !st.isPlaying) : st.error) end();
  });
  let gone = false;
  return {
    ended,
    play: async () => {
      await sound.playAsync().catch(end);
    },
    pause: async () => {
      await sound.pauseAsync().catch(() => undefined);
    },
    resume: async () => {
      await sound.playAsync().catch(() => undefined);
    },
    dispose: async () => {
      if (gone) return;
      gone = true;
      end();
      await sound.unloadAsync().catch(() => undefined);
    },
  };
}

class CouncilAudio {
  private queue: SpeakItem[] = [];
  private cache = new Map<string, Promise<string>>();
  private cur: Clip | null = null;
  private waiters: Array<() => void> = [];
  private gen = 0;
  private state: PlaybackState = { id: null, paused: false, loading: false };
  private subs = new Set<(s: PlaybackState) => void>();

  subscribe(fn: (s: PlaybackState) => void): () => void {
    this.subs.add(fn);
    fn(this.state);
    return () => {
      this.subs.delete(fn);
    };
  }
  get current(): PlaybackState {
    return this.state;
  }
  private set(next: Partial<PlaybackState>) {
    this.state = { ...this.state, ...next };
    this.subs.forEach((fn) => fn(this.state));
  }
  private audio(part: Part): Promise<string> {
    let p = this.cache.get(part.key);
    if (p) {
      this.cache.delete(part.key); // refresh recency
    } else {
      const fresh = fetchAudio(part.speaker, part.text);
      fresh.catch(() => {
        if (this.cache.get(part.key) === fresh) this.cache.delete(part.key);
      });
      p = fresh;
    }
    this.cache.set(part.key, p);
    while (this.cache.size > CACHE_MAX) {
      const oldest = this.cache.keys().next().value;
      if (oldest === undefined) break;
      this.cache.delete(oldest);
    }
    return p;
  }
  /** Whole-text parts (default) or sentence chunks (opts.chunked). */
  private parts(item: SpeakItem, chunked?: boolean): Part[] {
    if (!chunked) return [{ key: item.id, speaker: item.speaker, text: speakable(item.text) }];
    return splitSpeech(item.text).map((text, i) => ({ key: `${item.id}#${i}`, speaker: item.speaker, text }));
  }

  /** Queue a line (auto-speak). Its audio starts downloading right away. */
  enqueue(item: SpeakItem): void {
    for (const p of this.parts(item)) void this.audio(p).catch(() => undefined);
    this.queue.push(item);
    if (!this.state.id) void this.next();
  }

  /** Start downloading a line's audio without queueing it (chunked: only chunks 0 and 1). */
  prefetch(item: SpeakItem, opts?: { chunked?: boolean }): void {
    const parts = this.parts(item, opts?.chunked);
    for (const p of opts?.chunked ? parts.slice(0, 2) : parts) void this.audio(p).catch(() => undefined);
  }

  /** Play one message now (the Play button): clears the queue. */
  playNow(item: SpeakItem, opts?: { chunked?: boolean }): void {
    this.queue = [];
    void this.stopCurrent().then(() => this.start(item, opts?.chunked));
  }

  async togglePause(): Promise<void> {
    if (!this.state.id) return;
    const paused = !this.state.paused;
    const c = this.cur;
    if (c) await (paused ? c.pause() : c.resume());
    this.set({ paused });
    if (!paused) this.wake();
  }

  /** Stop everything (Conference off, room closed). */
  stop(): void {
    this.queue = [];
    void this.stopCurrent();
  }

  private wake() {
    const w = this.waiters;
    this.waiters = [];
    w.forEach((fn) => fn());
  }

  private async stopCurrent(): Promise<void> {
    this.gen++;
    const c = this.cur;
    this.cur = null;
    this.wake();
    if (c) await c.dispose();
    this.set({ id: null, paused: false, loading: false });
  }

  private async next(): Promise<void> {
    const item = this.queue.shift();
    if (item) await this.start(item);
  }

  /** Play an item's parts back to back; state.id stays item.id until the last one ends. */
  private async start(item: SpeakItem, chunked?: boolean): Promise<void> {
    const gen = ++this.gen;
    const parts = this.parts(item, chunked);
    this.set({ id: item.id, paused: false, loading: true });

    const prepared = new Map<number, Promise<Clip | null>>();
    const prepare = (i: number): Promise<Clip | null> => {
      let p = prepared.get(i);
      if (!p) {
        p = (async () => {
          try {
            const uri = await this.audio(parts[i]);
            if (gen !== this.gen) return null;
            const clip = await loadClip(uri);
            if (gen !== this.gen) {
              void clip.dispose();
              return null;
            }
            return clip;
          } catch {
            return null; // one failed clip never blocks the round
          }
        })();
        prepared.set(i, p);
      }
      return p;
    };

    try {
      for (let i = 0; i < parts.length && gen === this.gen; i++) {
        for (let j = i; j <= i + AHEAD && j < parts.length; j++) void this.audio(parts[j]).catch(() => undefined);
        const clip = await prepare(i);
        if (i + 1 < parts.length) void prepare(i + 1); // decode the next clip while this one plays
        if (!clip) continue;
        while (this.state.paused && gen === this.gen) await new Promise<void>((r) => this.waiters.push(r));
        if (gen !== this.gen) return;
        this.cur = clip;
        if (this.state.loading) this.set({ loading: false });
        await clip.play();
        await clip.ended;
        if (this.cur === clip) this.cur = null;
        await clip.dispose();
      }
    } finally {
      for (const p of prepared.values()) void p.then((c) => c?.dispose());
    }
    if (gen !== this.gen) return;
    this.cur = null;
    this.set({ id: null, paused: false, loading: false });
    void this.next();
  }
}

export const councilAudio = new CouncilAudio();
/** The compass sector deity's own player: the Council stops councilAudio whenever it mounts or goes
 *  inactive, which was cutting the sector rundowns off mid-sentence. */
export const sectorAudio = new CouncilAudio();
